import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  MenuItem,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/EditOutlined';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveIcon from '@mui/icons-material/UnarchiveOutlined';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import {
  addAlumniInteraction,
  archiveAlumni,
  getAlumni,
  getAlumniLookups,
  getAlumniTimeline,
  listAlumniInteractions,
  restoreAlumni,
} from '../../api/alumni';
import { useAuth } from '../../context/authContext';
import { PageHeader } from '../../components/PageHeader';
import { Alumni, AlumniInteractionType, AlumniTimelineEvent } from '../../types/alumni';
import {
  ALUMNI_STATUS_COLORS,
  ALUMNI_STATUS_LABELS,
  formatDateTime,
  formatDay,
  INTERACTION_LABELS,
  SOURCE_LABELS,
} from './alumniLabels';
import { AlumniFormDialog } from './AlumniFormDialog';

const errorText = (err: unknown, fallback: string) =>
  isAxiosError(err) ? (err.response?.data?.message ?? fallback) : fallback;

const person = (u: { firstName: string; lastName: string } | null | undefined) => (u ? `${u.firstName} ${u.lastName}` : '—');

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
      {value === null || value === undefined || value === '' ? '—' : value}
    </Typography>
  </Box>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Grid container columnSpacing={2}>
        {children}
      </Grid>
    </CardContent>
  </Card>
);

const half = (label: string, value: React.ReactNode) => (
  <Grid item xs={12} sm={6}>
    <Field label={label} value={value} />
  </Grid>
);
const full = (label: string, value: React.ReactNode) => (
  <Grid item xs={12}>
    <Field label={label} value={value} />
  </Grid>
);

const FIELD_LABELS: Record<string, string> = {
  fullName: 'Full name',
  phone: 'Contact number',
  email: 'Email',
  employer: 'Place of work',
  graduationYear: 'Graduation year',
  countryOfWorkId: 'Country of work',
  status: 'Status',
  jobTitle: 'Job title',
  lastVerifiedAt: 'Last verified',
  nextFollowUpAt: 'Next follow-up',
};

const describeEvent = (e: AlumniTimelineEvent) => {
  const nv = (e.newValue ?? {}) as Record<string, unknown>;
  switch (e.action) {
    case 'CREATED':
      return nv.source === 'GRADUATION' ? 'Created when the student was marked as graduated' : 'Record created';
    case 'ALUMNI_IMPORTED':
      return 'Record created by import';
    case 'EDITED':
      return `Edited: ${Object.keys(nv).map((k) => FIELD_LABELS[k] ?? k).join(', ')}`;
    case 'STATUS_CHANGED': {
      const from = (e.oldValue as { status?: string } | null)?.status;
      const to = nv.status as string | undefined;
      const label = (s?: string) => (s ? (ALUMNI_STATUS_LABELS as Record<string, string>)[s] ?? s : 'not set');
      return `Status changed from ${label(from)} to ${label(to)}`;
    }
    case 'ALUMNI_ARCHIVED':
      return `Archived${nv.reason ? ` — ${nv.reason}` : ''}`;
    case 'ALUMNI_RESTORED':
      return 'Restored from the archive';
    case 'ALUMNI_INTERACTION_LOGGED':
      return `Logged: ${INTERACTION_LABELS[nv.type as AlumniInteractionType] ?? nv.type}`;
    case 'ALUMNI_LINKED':
      return `Linked to student ${nv.studentNumber ?? ''}${nv.graduatedAt ? ' on graduation' : ''}`;
    default:
      return e.action;
  }
};

/** An alumni profile (Phase 105): every detail, the logged interactions
 * (with a form to add one), and the audit timeline. Edit, archive and
 * restore follow the user's alumni permissions; archived records are
 * read-only until restored. */
export const AlumniDetailPage = () => {
  const { id } = useParams();
  const alumniId = Number(id);
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState<'profile' | 'activity' | 'timeline'>('profile');
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [interaction, setInteraction] = useState<{ type: AlumniInteractionType; summary: string; occurredAt: string; nextFollowUpAt: string }>({
    type: 'CALL',
    summary: '',
    occurredAt: '',
    nextFollowUpAt: '',
  });

  const query = useQuery({ queryKey: ['alumni-record', alumniId], queryFn: () => getAlumni(alumniId), enabled: Number.isFinite(alumniId) });
  const lookups = useQuery({ queryKey: ['alumni-lookups'], queryFn: getAlumniLookups });
  const interactions = useQuery({ queryKey: ['alumni-interactions', alumniId], queryFn: () => listAlumniInteractions(alumniId), enabled: tab === 'activity' });
  const timeline = useQuery({ queryKey: ['alumni-timeline', alumniId], queryFn: () => getAlumniTimeline(alumniId), enabled: tab === 'timeline' });

  const refresh = () => {
    for (const key of ['alumni-record', 'alumni-interactions', 'alumni-timeline']) {
      queryClient.invalidateQueries({ queryKey: [key, alumniId] });
    }
    queryClient.invalidateQueries({ queryKey: ['alumni'] });
    queryClient.invalidateQueries({ queryKey: ['alumni-stats'] });
  };

  const archive = useMutation({
    mutationFn: () => archiveAlumni(alumniId, archiveReason.trim()),
    onSuccess: () => {
      setArchiveOpen(false);
      setArchiveReason('');
      refresh();
    },
  });
  const restore = useMutation({ mutationFn: () => restoreAlumni(alumniId), onSuccess: refresh });
  const logInteraction = useMutation({
    mutationFn: () =>
      addAlumniInteraction(alumniId, {
        type: interaction.type,
        summary: interaction.summary.trim(),
        ...(interaction.occurredAt ? { occurredAt: new Date(interaction.occurredAt).toISOString() } : {}),
        nextFollowUpAt: interaction.nextFollowUpAt ? new Date(interaction.nextFollowUpAt).toISOString() : null,
      }),
    onSuccess: () => {
      setInteraction({ type: 'CALL', summary: '', occurredAt: '', nextFollowUpAt: '' });
      refresh();
    },
  });

  if (query.isLoading) return <Skeleton variant="rounded" height={320} sx={{ m: 3 }} />;
  if (query.isError || !query.data) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {errorText(query.error, 'Alumni record not found')}.{' '}
        <Link component={RouterLink} to="/alumni">
          Back to alumni
        </Link>
      </Alert>
    );
  }
  const a: Alumni = query.data;
  const archived = !!a.archivedAt;
  const canEdit = hasPermission('alumni', 'update') && !archived;
  const canArchive = hasPermission('alumni', 'archive');

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Button component={RouterLink} to="/alumni" startIcon={<ArrowBackIcon />} sx={{ mb: 1 }}>
        Back to alumni
      </Button>
      <PageHeader
        icon={WorkspacePremiumIcon}
        title={a.fullName}
        subtitle={`${a.alumniNumber}${a.graduationYear ? ` · Graduated ${a.graduationYear}` : ''}${a.employer ? ` · ${a.employer}` : ''}`}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canEdit && (
              <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
            {canArchive && !archived && (
              <Button variant="outlined" color="warning" startIcon={<ArchiveIcon />} onClick={() => setArchiveOpen(true)}>
                Archive
              </Button>
            )}
            {canArchive && archived && (
              <Button variant="outlined" startIcon={<UnarchiveIcon />} disabled={restore.isPending} onClick={() => restore.mutate()}>
                Restore
              </Button>
            )}
          </Stack>
        }
      />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {a.status && <Chip color={ALUMNI_STATUS_COLORS[a.status]} label={ALUMNI_STATUS_LABELS[a.status]} />}
        <Chip variant="outlined" label={SOURCE_LABELS[a.source]} />
        {a.branch && <Chip variant="outlined" label={a.branch.name} />}
        {a.student && (
          <Chip
            component={RouterLink}
            to={`/students/${a.student.id}`}
            clickable
            color="primary"
            variant="outlined"
            label={`Student ${a.student.studentNumber}`}
          />
        )}
      </Stack>
      {archived && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Archived {formatDay(a.archivedAt)} by {person(a.archivedBy)}
          {a.archiveReason ? ` — ${a.archiveReason}` : ''}. The record is read-only until restored.
        </Alert>
      )}
      {restore.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorText(restore.error, "Couldn't restore the record")}</Alert>}

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 2 }}>
        <Tab value="profile" label="Profile" />
        <Tab value="activity" label="Activity & follow-ups" />
        <Tab value="timeline" label="Timeline" />
      </Tabs>

      {tab === 'profile' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Section title="Contact">
              {half('Contact number', a.phone)}
              {half('Email', a.email)}
              {half('WhatsApp', a.whatsappNumber)}
              {half('Alternative number', a.alternativePhone)}
              {half('Personal email', a.personalEmail)}
              {half('Work email', a.workEmail)}
              {half('Current city', a.currentCity)}
              {half(
                'LinkedIn',
                a.linkedinUrl ? (
                  <Link href={a.linkedinUrl} target="_blank" rel="noopener noreferrer">
                    {a.linkedinUrl}
                  </Link>
                ) : null,
              )}
              {half('Date of birth', a.dateOfBirth ? formatDay(a.dateOfBirth) : null)}
            </Section>
          </Grid>
          <Grid item xs={12} md={6}>
            <Section title="Employment">
              {half('Place of work', a.employer)}
              {half('Job title', a.jobTitle)}
              {half('Country of work', a.countryOfWork?.name)}
              {half('Specialization', a.specialization)}
              {half('Previous employer', a.previousEmployer)}
              {half('Status', a.status ? ALUMNI_STATUS_LABELS[a.status] : null)}
              {full('Employment history', a.employmentHistory)}
            </Section>
          </Grid>
          <Grid item xs={12} md={6}>
            <Section title="Study history">
              {half('Student ID', a.student?.studentNumber ?? a.originalStudentNumber)}
              {half('Graduation year', a.graduationYear)}
              {half('University', a.university)}
              {half('Course / programme', a.course)}
              {half('Intake', a.intake)}
              {half('Graduated (student record)', a.student?.graduatedAt ? formatDay(a.student.graduatedAt) : null)}
              {full('Further qualification', a.furtherQualification)}
            </Section>
          </Grid>
          <Grid item xs={12} md={6}>
            <Section title="Follow-up and record">
              {half('Details last verified', a.lastVerifiedAt ? formatDay(a.lastVerifiedAt) : 'Never')}
              {half('Next follow-up', a.nextFollowUpAt ? formatDay(a.nextFollowUpAt) : null)}
              {full('Notes', a.notes)}
              {half('Created', `${formatDateTime(a.createdAt)} by ${person(a.createdBy)}`)}
              {half('Last updated', `${formatDateTime(a.updatedAt)} by ${person(a.updatedBy ?? a.createdBy)}`)}
            </Section>
          </Grid>
        </Grid>
      )}

      {tab === 'activity' && (
        <Grid container spacing={2}>
          {hasPermission('alumni', 'update') && !archived && (
            <Grid item xs={12} md={5}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Log an interaction
                  </Typography>
                  <Stack spacing={1.5}>
                    <TextField select size="small" label="Type" value={interaction.type} onChange={(e) => setInteraction((s) => ({ ...s, type: e.target.value as AlumniInteractionType }))}>
                      {(Object.keys(INTERACTION_LABELS) as AlumniInteractionType[]).map((t) => (
                        <MenuItem key={t} value={t}>
                          {INTERACTION_LABELS[t]}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="What happened"
                      multiline
                      minRows={3}
                      size="small"
                      inputProps={{ maxLength: 5000 }}
                      value={interaction.summary}
                      onChange={(e) => setInteraction((s) => ({ ...s, summary: e.target.value }))}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <TextField type="date" size="small" label="Date" InputLabelProps={{ shrink: true }} fullWidth value={interaction.occurredAt} onChange={(e) => setInteraction((s) => ({ ...s, occurredAt: e.target.value }))} helperText="Today if empty" />
                      <TextField type="date" size="small" label="Next follow-up" InputLabelProps={{ shrink: true }} fullWidth value={interaction.nextFollowUpAt} onChange={(e) => setInteraction((s) => ({ ...s, nextFollowUpAt: e.target.value }))} helperText="Optional" />
                    </Stack>
                    {logInteraction.isError && <Alert severity="error">{errorText(logInteraction.error, "Couldn't save it")}</Alert>}
                    <Button variant="contained" disabled={interaction.summary.trim().length < 2 || logInteraction.isPending} onClick={() => logInteraction.mutate()}>
                      {logInteraction.isPending ? 'Saving…' : 'Save'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
          <Grid item xs={12} md={hasPermission('alumni', 'update') && !archived ? 7 : 12}>
            <Stack spacing={1.5}>
              {interactions.isLoading && <Skeleton variant="rounded" height={80} />}
              {interactions.data?.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No interactions logged yet.
                </Typography>
              )}
              {interactions.data?.map((i) => (
                <Card key={i.id} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
                      <Chip size="small" label={INTERACTION_LABELS[i.type]} color={i.type === 'CAREER_UPDATE' ? 'success' : 'default'} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDay(i.occurredAt)} · {person(i.createdBy)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                      {i.summary}
                    </Typography>
                    {i.nextFollowUpAt && (
                      <Typography variant="caption" color="text.secondary">
                        Follow up by {formatDay(i.nextFollowUpAt)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Grid>
        </Grid>
      )}

      {tab === 'timeline' && (
        <Card variant="outlined">
          <CardContent>
            {timeline.isLoading && <Skeleton variant="rounded" height={120} />}
            <Stack spacing={1.5} data-testid="alumni-timeline">
              {timeline.data?.map((e) => (
                <Box key={e.id} sx={{ borderLeft: '3px solid', borderColor: 'primary.light', pl: 1.5 }}>
                  <Typography variant="body2">{describeEvent(e)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(e.createdAt)} · {person(e.user)}
                  </Typography>
                </Box>
              ))}
              {timeline.data?.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No history yet.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <AlumniFormDialog open={editOpen} onClose={() => setEditOpen(false)} alumni={a} lookups={lookups.data} onSaved={refresh} />

      <Dialog open={archiveOpen} onClose={() => setArchiveOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Archive {a.alumniNumber}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The record is hidden from the active list and becomes read-only. Nothing is deleted, and it can be restored.
          </Typography>
          <TextField label="Reason" fullWidth multiline minRows={2} inputProps={{ maxLength: 500 }} value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} />
          {archive.isError && <Alert severity="error" sx={{ mt: 1 }}>{errorText(archive.error, "Couldn't archive it")}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={archiveReason.trim().length < 3 || archive.isPending} onClick={() => archive.mutate()}>
            {archive.isPending ? 'Archiving…' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
